import {Container, Spacer} from '@nfq/react-grid';

import {Button} from 'UI/components/action/Button';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'BackSection'
     */
    testId?: string;
}

/**
 * A simple back navigation section that renders a styled "Zurück" (Back) button.
 * The `BackSection` component provides a consistent layout for returning to the
 * homepage or previous page. It includes vertical spacing above and below the
 * button for visual balance. The button is styled as a secondary variant and
 * uses a link to navigate to the root (`'/'`).
 * The component also applies a `data-cy` attribute for reliable end-to-end
 * test targeting.
 *
 * @param props        The component props.
 * @param props.testId An optional identifier used for testing, applied as `data-cy`. Defaults to `'BackSection'`.
 * @returns A JSX element rendering the back navigation section.
 *
 * @example
 * ```tsx
 * <BackSection />
 * ```
 */
const BackSection = ({testId = 'BackSection'}: ComponentProps) => (
    <Container data-cy={testId}>
        <Spacer y={24} />
        <Button as="link" href="/" variant="secondary">Zurück</Button>
        <Spacer y={10} />
    </Container>
);

BackSection.displayName = 'BackSection';

export {BackSection};